import { useEffect, useState, useCallback } from 'react';

import usePersonContactData from '../../state/personAcknowledgement/UsePersonContactData';
import useSecurityData from '../../state/security/UseSecurityData';
import { isValidEmail, isValidUSOrCanadianPhoneNumber, isValidMexicanPhoneNumber } from '../../utils/validation';

const INITIAL_STATE = {
  isLoading: false,
  showModal: false,
  contactData: null,
  errorMessage: '',
  isEditing: false,
  formState: {
    emailAddress: '',
    phoneNumber: ''
  },
  errorState: {
    emailAddress: '',
    phoneNumber: ''
  }
};

const useContactConfirmation = (personId, onConfirmed, onCanceled) => {
  const [state, setState] = useState({ ...INITIAL_STATE });
  const { personContactState, getPersonContact, updatePersonContact, confirmPersonContact } = usePersonContactData();
  const { bustSecurityCache } = useSecurityData();
  const [hasLoaded, setHasLoaded] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);

  const showConfirmationModal = () => {
    setState(prev => ({
      ...prev,
      showModal: true,
      isLoading: true,
      errorMessage: ''
    }));
    
    if (personId) {
      getPersonContact(personId);
    }
  };

  const onConfirmClicked = () => {
    showConfirmationModal();
  };

  const onEditClicked = () => {
    console.log('Edit clicked, current contactData:', state.contactData);
    
    // Use the existing phone number as-is, only add +1 if it's completely empty
    let phoneValue = state.contactData?.phoneNumber || '';
    
    // Only add +1 if the phone number is completely empty or null
    if (!phoneValue || phoneValue.trim() === '') {
      phoneValue = '+1'; // Default US country code only for empty values
    }
    // If phone number exists, use it as-is (don't modify existing numbers)
    
    setState(prev => {
      const newFormState = {
        emailAddress: prev.contactData?.emailAddress || '',
        phoneNumber: phoneValue
      };
      console.log('Setting formState to:', newFormState);
      return {
        ...prev,
        isEditing: true,
        formState: newFormState,
        errorState: {
          emailAddress: '',
          phoneNumber: ''
        }
      };
    });
  };

  const onCancelEditClicked = () => {
    setState(prev => ({
      ...prev,
      isEditing: false,
      errorState: {
        emailAddress: '',
        phoneNumber: ''
      }
    }));
  };

  const onFieldChanged = (fieldName, value) => {
    console.log('onFieldChanged called:', fieldName, value);
    setState(prev => {
      const newState = {
        ...prev,
        formState: {
          ...prev.formState,
          [fieldName]: value
        },
        errorState: {
          ...prev.errorState,
          [fieldName]: '' // Clear error when user starts typing
        }
      };
      console.log('New formState after change:', newState.formState);
      return newState;
    });
  };

  // Add specific phone number handler following MFAForm pattern
  const onPhoneNumberChanged = (value) => {
    console.log('onPhoneNumberChanged called:', value);
    setState(prev => {
      const newState = {
        ...prev,
        formState: {
          ...prev.formState,
          phoneNumber: value
        },
        errorState: {
          ...prev.errorState,
          phoneNumber: '' // Clear error when user starts typing
        }
      };
      console.log('New formState after phone change:', newState.formState);
      return newState;
    });
  };

  const validateForm = () => {
    const errors = {};
    
    // Email validation
    if (!state.formState.emailAddress?.trim()) {
      errors.emailAddress = 'Email address is required';
    } else if (!isValidEmail(state.formState.emailAddress.trim())) {
      errors.emailAddress = 'Please enter a valid email address';
    }
    
    // Phone number validation - same logic as MFAFormValidation
    if (state.formState.phoneNumber && state.formState.phoneNumber.trim() && state.formState.phoneNumber !== '+1') {
      const phoneNumber = state.formState.phoneNumber.trim();
      if (!isValidUSOrCanadianPhoneNumber(phoneNumber) && !isValidMexicanPhoneNumber(phoneNumber)) {
        errors.phoneNumber = 'Must enter a valid US, Canadian, or Mexican phone number in +1/+52 (XXX) XXX-XXXX format';
      }
    }
    
    setState(prev => ({
      ...prev,
      errorState: errors
    }));
    
    return Object.keys(errors).length === 0;
  };

  const onSaveClicked = () => {
    if (validateForm()) {
      setState(prev => ({
        ...prev,
        isLoading: true
      }));
      
      // Call API to update contact information
      if (updatePersonContact) {
        updatePersonContact(personId, {
          emailAddress: state.formState.emailAddress.trim(),
          phoneNumber: state.formState.phoneNumber?.trim() || ''
        });
      } else {
        // If no update function, just update local state
        setState(prev => ({
          ...prev,
          isLoading: false,
          isEditing: false,
          contactData: {
            ...prev.contactData,
            emailAddress: prev.formState.emailAddress.trim(),
            phoneNumber: prev.formState.phoneNumber?.trim() || ''
          }
        }));
      }
    }
  };

  const onModalConfirmed = () => {
    setState(prev => ({
      ...prev,
      isLoading: true
    }));
    
    setIsConfirming(true);
    console.log('Confirming contact info for personId:', personId);
    
    // Call API to mark contact info as confirmed
    if (confirmPersonContact) {
      confirmPersonContact(personId);
    }
  };

  const onModalCanceled = () => {
    setState(prev => ({
      ...prev,
      showModal: false
    }));
    
    if (onCanceled) {
      onCanceled('cancel');
    }
  };

  // Handle API response
  useEffect(() => {
    console.log('personContactState changed:', personContactState);
    
    if (personContactState.isObjLoading !== true) {
      if (personContactState.objData) {
        console.log('API Response objData:', personContactState.objData);
        
        // Check if this was a response to the confirm contact API call
        if (isConfirming && personContactState.objData) {
          console.log('Contact confirmation successful, busting security cache...');
          
          // Bust the security cache to refresh user info with updated hasConfirmedContactInfo
          bustSecurityCache();
          
          setState(prev => ({
            ...prev,
            isLoading: false,
            showModal: false
          }));
          
          setIsConfirming(false);
          
          if (onConfirmed) {
            onConfirmed(state.contactData);
          }
          
          return;
        }
        
        // Try to extract contact data - adjust these field names based on actual API response
        const contactData = {
          emailAddress: personContactState.objData.emailAddress || 
                 personContactState.objData.email || 
                 personContactState.objData.Email ||
                 personContactState.objData.EmailAddress,
          phoneNumber: personContactState.objData.phoneNumber || 
                      personContactState.objData.phone || 
                      personContactState.objData.PhoneNumber ||
                      personContactState.objData.Phone ||
                      '+1' // Default value if no phone number
        };
        
        console.log('Extracted contact data:', contactData);
        
        setState(prev => ({
          ...prev,
          isLoading: false,
          contactData: contactData,
          isEditing: false // Reset editing state after save
        }));
        setHasLoaded(true);
      } else if (personContactState.message && !personContactState.isObjLoading) {
        console.log('API Error message:', personContactState.message);
        setState(prev => ({
          ...prev,
          isLoading: false,
          errorMessage: personContactState.message
        }));
        setHasLoaded(true);
      }
    }
  }, [personContactState, isConfirming]); // Removed problematic dependencies

  // Separate effect to handle the confirmation success callback
  useEffect(() => {
    if (!isConfirming && state.contactData && onConfirmed) {
      // This runs after confirmation is complete and contactData is available
      // But we don't want to call onConfirmed here automatically
    }
  }, [isConfirming, state.contactData]); // Don't include onConfirmed in deps to avoid loops

  // Auto-load contact data when personId is provided (only once)
  useEffect(() => {
    if (personId && !hasLoaded && !personContactState.isObjLoading) {
      console.log('Auto-loading contact data for personId:', personId);
      setState(prev => ({
        ...prev,
        isLoading: true,
        errorMessage: ''
      }));
      getPersonContact(personId);
    }
  }, [personId, hasLoaded, personContactState.isObjLoading]);

  return {
    ...state,
    onConfirmClicked,
    onEditClicked,
    onSaveClicked,
    onCancelEditClicked,
    onModalConfirmed,
    onModalCanceled,
    onFieldChanged,
    onPhoneNumberChanged,
    showConfirmationModal
  };
};

export default useContactConfirmation;